import React from "react";
import localforage from "localforage";
import {
  getNegotiationModeRevision,
  isNegotiationModeReady,
  negotiateAPI,
  onRTAReconnect,
  RTA,
  sessionInfo,
} from "../../services/negotiationBridge";
import {
  parseNegotiationNotices,
  type NegotiationNotices,
} from "../../services/negotiationNotices";

/** HTTP owns state. RTA carries only a company-scoped hint to re-read it. */
export function useNegotiationNotices(enabled: boolean, resetKey: number, windowKey?: string) {
  const [state, setState] = React.useState<{
    key: number;
    windowKey?: string;
    snapshot: NegotiationNotices | null;
    stale: boolean;
  }>({ key: resetKey, windowKey, snapshot: null, stale: false });
  const refreshRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    let disposed = false;
    let request: AbortController | undefined;
    let event: string | undefined;
    let hintTimer: ReturnType<typeof setTimeout> | undefined;
    setState({ key: resetKey, windowKey, snapshot: null, stale: false });
    if (!enabled) {
      refreshRef.current = () => {};
      return;
    }
    const current = () =>
      !disposed && isNegotiationModeReady() && resetKey === getNegotiationModeRevision();
    const read = async () => {
      if (!current()) return;
      request?.abort();
      const active = new AbortController();
      request = active;
      const get = negotiateAPI.get;
      try {
        const jwt = await localforage.getItem<string>("JWT");
        if (!jwt || !current() || active.signal.aborted) return;
        const response = await get("/notices", {
          headers: { "X-JWT": jwt },
          signal: active.signal,
          timeout: 10_000,
        });
        if (!current() || active.signal.aborted) return;
        const snapshot = parseNegotiationNotices(response.data);
        if (!snapshot) throw new Error("Invalid negotiation notices");
        setState({ key: resetKey, windowKey, snapshot, stale: false });
      } catch {
        if (!current() || active.signal.aborted) return;
        setState((previous) => ({ ...previous, stale: true }));
      }
    };
    const refresh = () => void read();
    const hint = () => {
      clearTimeout(hintTimer);
      hintTimer = setTimeout(refresh, 150);
    };
    refreshRef.current = refresh;
    refresh();
    void sessionInfo()
      .then((info) => {
        if (!info || !current()) return;
        event = `negotiationNoticesChanged-${info.org_id}`;
        RTA.on(event, hint);
      })
      .catch(() => {});
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 15_000);
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", visible);
    const unsubscribe = onRTAReconnect(refresh);
    RTA.on("addNewBid", hint);
    RTA.on("negotiateMarket", hint);
    return () => {
      disposed = true;
      request?.abort();
      clearInterval(timer);
      clearTimeout(hintTimer);
      refreshRef.current = () => {};
      document.removeEventListener("visibilitychange", visible);
      unsubscribe();
      if (event) RTA.off(event, hint);
      RTA.off("addNewBid", hint);
      RTA.off("negotiateMarket", hint);
    };
  }, [enabled, resetKey, windowKey]);
  const refresh = React.useCallback(() => refreshRef.current(), []);
  return {
    snapshot:
      state.key === resetKey && state.windowKey === windowKey && enabled ? state.snapshot : null,
    stale: state.key === resetKey && state.stale,
    refresh,
  };
}
